import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="chat-taxonomy-inline-save"
//
// Generic inline save: posts a textarea's content (plus a fixed payload of
// extra params) to a URL via fetch — no <form> tag involved. The keyword
// editor and rules editor live inside Trestle's outer resource <form>, and
// HTML5 disallows nested forms: the inner <form> start tag is dropped by
// the parser and the inner </form> closes the OUTER form, so the first
// inline save button used to silently submit the resource update instead
// of update_keywords / update_rules — losing the user's edits.
//
// Targets:
//   - textarea: the <textarea> with the user input (required)
//   - status:   inline status text (optional)
//   - count:    inline entry-count badge to update on success (optional)
//
// Values:
//   - url:        POST endpoint
//   - payload:    JSON object of additional params (target_kind, target_key,
//                 sub_context_key, locale, …)
//   - countLabel: word that follows the count number (default "Einträge")
export default class extends Controller {
  static targets = ['textarea', 'status', 'count'];
  static values = {
    url: String,
    payload: Object,
    countLabel: { type: String, default: 'Einträge' },
  };

  async submit(event) {
    event.preventDefault();
    if (this.saving) return;

    this.saving = true;
    const button = event.currentTarget;
    button.disabled = true;
    this.setStatus('Speichere…', 'info');

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const data = new FormData();
    data.append('value', this.textareaTarget.value);
    Object.entries(this.payloadValue || {}).forEach(([key, val]) => {
      if (val == null || val === '') return;
      data.append(key, val);
    });
    if (csrfToken) data.append('authenticity_token', csrfToken);

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        body: data,
        headers: {
          'X-CSRF-Token': csrfToken || '',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      const contentType = response.headers.get('content-type') || '';
      const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : null;

      if (response.ok && payload && payload.ok) {
        this.setStatus(payload.message || 'Gespeichert ✓', 'success');
        if (this.hasCountTarget && typeof payload.count === 'number') {
          this.countTarget.textContent = `${payload.count} ${this.countLabelValue}`;
        }
      } else {
        const errorMsg = (payload && payload.error) || `HTTP ${response.status}`;
        this.setStatus(`Fehler: ${errorMsg}`, 'error');
      }
    } catch (err) {
      this.setStatus(`Fehler: ${err.message}`, 'error');
    } finally {
      this.saving = false;
      button.disabled = false;
    }
  }

  setStatus(message, kind) {
    if (!this.hasStatusTarget) return;
    const colors = {
      info: '#0c5460',
      success: '#155724',
      warning: '#856404',
      error: '#721c24',
    };
    this.statusTarget.style.color = colors[kind] || '#333';
    this.statusTarget.textContent = message;
  }
}
